<template>
  <v-dialog
    v-if="value"
    :value="value" @input="input => $emit('input', input)"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
  >
    <div style="min-height: 100%; overflow-y: hidden; background-color: #f1f3f4;" class="d-flex flex-column">
      <header class="pa-4 white d-flex align-center" style="font-size: 1.3em; height: 64px;">
        <v-btn icon @click="$emit('input', false)" class="mr-2">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <div>{{link.name}}</div>
        <v-spacer></v-spacer>
        <div v-if="advancedSeries" style="width: 250px;" class="d-flex align-center">
          <zp-date-range-picker label="Período" v-model="range" :min="value.created_at" clearable hide-details></zp-date-range-picker>
        </div>
        <div style="width: 220px;" v-else>
          <v-select
            outlined
            dense
            :loading="loading"
            label="Período"
            v-model="days"
            :items="periods"
            hide-details
          >
            <template v-slot:item="{ item }">
              <div class="caption d-flex flex-column pb-1 pt-1">
                <span class="font-weight-bold">{{item.text}}</span>
                <span>{{item.description}}</span>
              </div>
            </template>
          </v-select>
        </div>
      </header>
      <v-divider></v-divider>
      <div class="d-flex flex-column pa-4 flex-grow-1" style="overflow-y: auto;">
        <div v-if="!isActivePlan" class="d-flex justify-center flex-grow-1 align-center">
          <div class="d-flex flex-column">
            <v-icon x-large>mdi-chart-box</v-icon>
            <strong style="font-size: .9em;" class="mt-3">Contrate um de nossos planos para acompanhar seus indicadores :)</strong>
          </div>
        </div>
        <div v-else-if="loading || !(link.clicks || []).length" class="d-flex justify-center flex-grow-1 align-center">
          <v-progress-circular
            v-if="loading"
            :width="3"
            :size="60"
            color="blue"
            indeterminate
          ></v-progress-circular>
          <div v-else class="d-flex flex-column">
            <v-icon x-large>mdi-link-variant-off</v-icon>
            <strong style="font-size: .8em;" class="mt-3">Não há informações de desempenho deste período</strong>
          </div>
        </div>
        <div v-else>
          <v-row>
            <v-col cols="12" md="3" v-if="link.mode !== 'direct'">
              <zp-dash-card
                icon="mdi-mouse"
                title="Acessos"
                color="info"
                :value="cards.clicks"
              >
                <template v-slot:legend>
                  <div class="d-flex justify-space-between">
                    <div class="d-flex flex-column">
                      <span>Acessos diretos</span>
                      <span>Acessos com UTMs</span>
                    </div>
                    <div class="d-flex flex-column text-right">
                      <span>{{cards.direct.clicks | number}}</span>
                      <span>{{cards.utms.clicks | number}}</span>
                    </div>
                  </div>
                </template>
              </zp-dash-card>
            </v-col>
            <v-col cols="12" md="3">
              <zp-dash-card
                icon="mdi-check-circle"
                title="Redirecionamentos"
                color="green"
                :value="cards.redirects"
              >
                <template v-slot:legend>
                  <div class="d-flex justify-space-between">
                    <div class="d-flex flex-column">
                      <span>Redirecionamentos diretos</span>
                      <span>Redirecionamentos com UTMs</span>
                    </div>
                    <div class="d-flex flex-column text-right">
                      <span>{{cards.direct.redirects | number}}</span>
                      <span>{{cards.utms.redirects | number}}</span>
                    </div>
                  </div>
                </template>
              </zp-dash-card>
            </v-col>
          </v-row>
          <v-row>
            <v-col md="12">
              <zp-chart-desempenho-geral :link="linkData" :range="range"></zp-chart-desempenho-geral>
            </v-col>
          </v-row>
          <v-row>
            <v-col md="4">
              <zp-chart-acessos-por-dispositivo :link="linkData"></zp-chart-acessos-por-dispositivo>
            </v-col>
            <v-col md="4">
              <zp-chart-acessos-por-sistema-operacional :link="linkData"></zp-chart-acessos-por-sistema-operacional>
            </v-col>
          </v-row>
          <template v-if="type === 'utm'">
            <v-row>
              <v-col md="12">
                <zp-chart-origem-da-campanha :link="linkData" :range="range" :utm="utm" @change="source => updateUTM('source', source)"></zp-chart-origem-da-campanha>
              </v-col>
              <v-col md="12">
                <zp-chart-meio-da-campanha :link="linkData" :utm="utm" :range="range" @change="medium => updateUTM('medium', medium)"></zp-chart-meio-da-campanha>
              </v-col>
            </v-row>
            <v-row>
              <v-col md="12">
                <zp-chart-campanhas :link="linkData" :utm="utm" :range="range" @change="campaign => updateUTM('campaign', campaign)"></zp-chart-campanhas>
              </v-col>
              <v-col md="12">
                <zp-chart-conteudo-do-anuncio :link="linkData" :range="range" :utm="utm" @change="content => updateUTM('content', content)"></zp-chart-conteudo-do-anuncio>
              </v-col>
            </v-row>
          </template>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import moment from 'moment'
import { mapGetters, mapState } from 'vuex'

import ZpDateRangePicker from '../DateRangePicker'
import ZpDashCard from '../DashCard'
import ZpChartDesempenhoGeral from './DesempenhoGeral'
import ZpChartAcessosPorDispositivo from './AcessosPorDispositivo'
import ZpChartAcessosPorSistemaOperacional from './AcessosPorSistemaOperacional'
import ZpChartMeioDaCampanha from './MeioDaCampanha'
import ZpChartOrigemDaCampanha from './OrigemDaCampanha'
import ZpChartCampanhas from './Campanhas'
import ZpChartConteudoDoAnuncio from './ConteudoDoAnuncio'

export default {
  props: ['value'],
  components: {
    ZpDateRangePicker,
    ZpDashCard,
    ZpChartDesempenhoGeral,
    ZpChartAcessosPorDispositivo,
    ZpChartOrigemDaCampanha,
    ZpChartMeioDaCampanha,
    ZpChartAcessosPorSistemaOperacional,
    ZpChartCampanhas,
    ZpChartConteudoDoAnuncio
  },
  data () {
    return {
      desempenho: true,
      loading: false,
      days: 7,
      advancedSeries: false,
      link: {},
      utm: {},
      type: 'utm',
      range: [
        moment().subtract('7', 'days').toISOString(),
        moment().toISOString()
      ]
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user
    }),
    ...mapGetters({
      project: 'projects/getSelected'
    }),
    periods () {
      const periods = [{
        text: 'Máximo',
        description: `${moment(this.value.created_at).format('DD/MM/YY')} - ${moment().format('DD/MM/YY')}`,
        value: 'max'
      }, {
        text: 'Hoje',
        description: moment().format('DD/MM/YY'),
        value: 'today'
      }, {
        text: 'Ontem',
        description: moment().subtract(1, 'days').format('DD/MM/YY'),
        value: 'yersteday'
      }, {
        text: 'Últimos 7 dias',
        description: `${moment().subtract(7, 'days').format('DD/MM/YY')} - ${moment().format('DD/MM/YY')}`,
        value: 7
      }, {
        text: 'Últimos 30 dias',
        description: `${moment().subtract(30, 'days').format('DD/MM/YY')} - ${moment().format('DD/MM/YY')}`,
        value: 30
      }, {
        text: 'Esta semana',
        description: `${moment().weekday(0).format('DD/MM/YY')} - ${moment().weekday(7).format('DD/MM/YY')}`,
        value: 'week'
      }, {
        text: 'Este mês',
        description: `${moment().format('[01]/MM/YY')} - ` + moment().format(`[${moment().daysInMonth()}]/MM/YY`),
        value: 'month'
      }, {
        value: -1,
        text: 'Customizado'
      }]

      return periods
    },
    isActivePlan () {
      return moment(this.user.active_plan_at || 0).valueOf() > Date.now() || this.user.id !== this.project.user_id
    },
    linkData () {
      return {
        ...this.link,
        clicks: (this.link.clicks || [])
      }
    },
    cards () {
      return (this.linkData.clicks || []).reduce((data, item) => {
        data.clicks += item.views || 0
        data.redirects += item.redirects || 0
        
        if (item.utm_source) {
          data.utms.clicks += item.views || 0
          data.utms.redirects += item.redirects || 0
        } else {
          data.direct.clicks += item.views || 0
          data.direct.redirects += item.redirects || 0
        }

        return data
      }, {
        clicks: 0,
        redirects: 0,
        direct: {
          clicks: 0,
          redirects: 0
        },
        utms: {
          clicks: 0,
          redirects: 0
        }
      })
    }
  },
  watch: {
    value: {
      immediate: true,
      handler: async function (value, _value) {
        if (value && !_value) {
          this.search(value)
        } else if (!value) {
          this.days = 7
        }
      }
    },
    range: {
      deep: true,
      handler: async function (range) {
        if (range.length === 2 && this.value) {
          setImmediate(() => this.search())
        } else if (!range.length) {
          this.days = 7
          this.advancedSeries = false
        }
      }
    },
    days: function (days) {
      if (days === -1) {
        this.advancedSeries = true
      } else if (days === 'max') {
        this.range = [
          this.value.created_at,
          moment().toISOString()
        ]
      } else if (days === 'today') {
        this.range = [
          moment().toISOString(),
          moment().toISOString()
        ]
      } else if (days === 'yersteday') {
        this.range = [
          moment().subtract(1, 'days').toISOString(),
          moment().subtract(1, 'days').toISOString()
        ]
      } else if (days === 'week') {
        this.period = [
          moment().weekday(0).toISOString(),
          moment(moment().format(`YYYY-MM-[${moment().daysInMonth()}]`)).toISOString()
        ]
      } else if (days === 'month') {
        this.period = [
          moment(moment().format('YYYY-MM-[01]')).toISOString(),
          moment(moment().format(`YYYY-MM-[${moment().daysInMonth()}]`)).toISOString()
        ]
      } else {
        this.range = [
          moment().subtract(days, 'days').toISOString(),
          moment().toISOString()
        ]
      }

      this.desempenho = false
      setImmediate(() => this.desempenho = true)
    }
  },
  methods: {
    async search (value = this.value) {
      this.loading = true
      try {
        this.link = await this.$store.dispatch('links/show', {
          project_id: value.project_id,
          id: `${value.id}?start_at=${moment(this.range[0]).utc().format('YYYY-MM-DD [00:00]')}&end_at=${moment(this.range[1]).utc().format('YYYY-MM-DD [23:59]')}`
        })
      } catch (e) {
        alert(e.message)
      }
      this.loading = false
    },
    updateUTM (name, value) {
      this.utm = {
        ...this.utm,
        [name]: value
      }
    }
  }
}
</script>

<style lang="scss" scoped>

</style>